import { useCallback, useEffect, useRef, useState } from "react";

const THRESHOLD = 80;
const MAX_PULL = 120;
const RESISTANCE = 0.4;

export function PullToRefresh({ children }: { children: React.ReactNode }) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const canPull = useCallback(() => {
    return window.scrollY <= 0;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handleTouchStart(e: TouchEvent) {
      if (!canPull() || refreshing) return;
      startY.current = e.touches[0].clientY;
      currentY.current = startY.current;
    }

    function handleTouchMove(e: TouchEvent) {
      if (!canPull() || refreshing) return;

      currentY.current = e.touches[0].clientY;
      const distance = Math.max(0, currentY.current - startY.current);

      if (distance > 0) {
        const dampened = Math.min(distance * RESISTANCE, MAX_PULL);
        setPullDistance(dampened);
        setPulling(dampened > 10);

        if (dampened > 10) {
          e.preventDefault();
        }
      }
    }

    function handleTouchEnd() {
      if (!pulling) {
        setPullDistance(0);
        return;
      }

      if (pullDistance >= THRESHOLD) {
        setRefreshing(true);
        setPullDistance(THRESHOLD);
        window.location.reload();
      } else {
        setPullDistance(0);
        setPulling(false);
      }
    }

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [canPull, pulling, pullDistance, refreshing]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = progress * 360;

  return (
    <div ref={containerRef} className="relative min-h-screen">
      <div
        className="pointer-events-none fixed left-1/2 z-[60] -translate-x-1/2 transition-opacity duration-200"
        style={{
          top: `max(env(safe-area-inset-top, 0px), ${pullDistance - 40}px)`,
          opacity: pulling ? 1 : 0,
        }}
      >
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-gray-100 ${
            refreshing ? "animate-spin" : ""
          }`}
          style={{
            transform: refreshing ? undefined : `rotate(${rotation}deg)`,
          }}
        >
          <svg
            className="h-5 w-5 text-primary-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"
            />
          </svg>
        </div>
      </div>

      <div
        style={{
          transform: pulling ? `translateY(${pullDistance}px)` : undefined,
          transition: pulling ? "none" : "transform 0.3s ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
}
